import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Layers, PlusCircle, MinusCircle } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const defaultFeeStructures = () => [
  { code: "monthly",   label: "Monthly",   amount: 3500, period_label: "per month",   gst_percent: 0, notes: "Standard monthly fee" },
  { code: "quarterly", label: "Quarterly", amount: 9500, period_label: "per quarter", gst_percent: 0, notes: "Save vs monthly" },
];

const empty = () => ({
  sport: "cricket", age_group: "U-14", name: "", coach: "",
  days: ["Mon", "Wed", "Fri"], time_start: "16:00", time_end: "18:00",
  capacity: 20, monthly_fee: 3500, quarterly_fee: 9500, active: true,
  registration_fee: 500, admission_fee: 2000, security_deposit: 3000, kit_fee: 1500,
  fee_structures: defaultFeeStructures(),
});

export default function AdminBatches() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [coaches, setCoaches] = useState([]);

  const load = () => api.get("/admin/batches").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/coaches").then((r) => setCoaches(r.data.filter((c) => c.active))).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        capacity: parseInt(form.capacity),
        monthly_fee: parseFloat(form.monthly_fee),
        quarterly_fee: parseFloat(form.quarterly_fee),
        registration_fee: parseFloat(form.registration_fee || 0),
        admission_fee: parseFloat(form.admission_fee || 0),
        security_deposit: parseFloat(form.security_deposit || 0),
        kit_fee: parseFloat(form.kit_fee || 0),
        fee_structures: (form.fee_structures || []).map((fs) => ({
          ...fs,
          amount: parseFloat(fs.amount) || 0,
          gst_percent: parseFloat(fs.gst_percent) || 0,
        })),
      };
      if (editing?.id) payload.id = editing.id;
      await api.post("/admin/batches", payload);
      toast.success("Batch saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this batch?")) return;
    await api.delete(`/admin/batches/${id}`);
    load();
  };

  const toggleDay = (d) => {
    setForm({ ...form, days: form.days.includes(d) ? form.days.filter((x) => x !== d) : [...form.days, d] });
  };

  const updateFee = (i, key, val) => {
    const next = [...(form.fee_structures || [])];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, fee_structures: next });
  };
  const addFee = () => {
    setForm({
      ...form,
      fee_structures: [
        ...(form.fee_structures || []),
        { code: `custom_${Date.now()}`, label: "Custom", amount: 0, period_label: "per month", gst_percent: 0, notes: "" },
      ],
    });
  };
  const delFee = (i) => {
    const next = [...(form.fee_structures || [])];
    next.splice(i, 1);
    setForm({ ...form, fee_structures: next });
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...b, fee_structures: b.fee_structures?.length ? b.fee_structures : defaultFeeStructures() });
  };

  return (
    <div data-testid="admin-batches">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Academy · Batches</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Academy Batches.</h1>
        </div>
        <button onClick={() => { setEditing({}); setForm(empty()); }} data-testid="new-batch-btn" className="bg-brand-blue text-white px-5 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
          <Plus size={12} /> New Batch
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.id} className="border border-white/12 bg-[#131A2B] p-5" data-testid={`admin-batch-${b.id}`}>
            <div className="flex justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{b.sport} · {b.age_group}</div>
                <div className="font-display text-xl uppercase font-bold mt-1 truncate">{b.name}</div>
                <div className="text-xs text-white/50 mt-2">Coach: {b.coach}</div>
                <div className="text-xs text-white/50 font-mono">{b.days.join("·")} · {b.time_start}–{b.time_end}</div>
                <div className="mt-3 space-y-0.5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 flex items-center gap-1"><Layers size={10} /> {(b.fee_structures || []).length || 2} fee structures</div>
                  {(b.fee_structures || []).slice(0, 4).map((fs) => (
                    <div key={fs.code} className="text-xs font-mono flex justify-between gap-2 border-l-2 border-brand-blue/30 pl-2 py-0.5">
                      <span className="text-white/70 truncate">{fs.label}</span>
                      <span className="brand-blue">{formatINR(fs.amount)}</span>
                    </div>
                  ))}
                  {(!b.fee_structures || b.fee_structures.length === 0) && (
                    <div className="text-xs brand-blue font-mono">{formatINR(b.monthly_fee)}/mo · {formatINR(b.quarterly_fee)}/qtr</div>
                  )}
                </div>
              </div>
              <div className="space-y-1 shrink-0">
                <button onClick={() => openEdit(b)} data-testid={`edit-batch-${b.id}`} className="p-1 text-white/60 hover:text-brand-orange"><Edit3 size={14} /></button>
                <button onClick={() => del(b.id)} className="p-1 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">{editing.id ? "Edit" : "New"} Batch</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="bg-transparent border border-white/20 py-3 px-3">
                <option>cricket</option><option>football</option><option>badminton</option><option>swimming</option>
              </select>
              <input value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} placeholder="Age group" className="bg-transparent border border-white/20 py-3 px-3" />
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Batch name" className="bg-transparent border border-white/20 py-3 px-3 col-span-2" data-testid="batch-name" />
              <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={form.coach} onChange={(e) => setForm({ ...form, coach: e.target.value })} placeholder="Coach display name" className="bg-transparent border border-white/20 py-3 px-3" />
                <select
                  value={form.coach_id || ""}
                  onChange={(e) => {
                    const cid = e.target.value || null;
                    const picked = coaches.find((c) => c.id === cid);
                    setForm({ ...form, coach_id: cid, coach: picked?.name || form.coach });
                  }}
                  data-testid="batch-coach-picker"
                  className="bg-[#131A2B] border border-white/20 py-3 px-3"
                >
                  <option value="">— Assign coach login (optional) —</option>
                  {coaches.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
                  ))}
                </select>
              </div>
              <input type="time" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} className="bg-transparent border border-white/20 py-3 px-3" />
              <input type="time" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} className="bg-transparent border border-white/20 py-3 px-3" />
              <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="Capacity" className="bg-transparent border border-white/20 py-3 px-3" />
              <div className="col-span-2 flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <button key={d} type="button" onClick={() => toggleDay(d)} className={`px-3 py-1.5 text-xs font-mono border ${form.days.includes(d) ? "bg-brand-blue text-white border-brand-blue" : "border-white/20 text-white/50"}`}>{d}</button>
                ))}
              </div>

              {/* Legacy fee fields (kept for backwards compat + fallback) */}
              <input type="number" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} placeholder="Legacy monthly fee (₹)" className="bg-transparent border border-white/20 py-3 px-3" />
              <input type="number" value={form.quarterly_fee} onChange={(e) => setForm({ ...form, quarterly_fee: e.target.value })} placeholder="Legacy quarterly fee (₹)" className="bg-transparent border border-white/20 py-3 px-3" />
            </div>

            {/* One-time enrollment-day fees. GST applies unless refundable. */}
            <div className="mt-6" data-testid="batch-onetime-fees">
              <div className="text-[10px] uppercase tracking-widest brand-orange font-mono mb-1">// Standard billing for new admissions — editable per student at enrolment</div>
              <p className="text-[11px] text-white/50 mb-3">GST 18% applies to non-refundable fees. Security deposit is refundable and NEVER taxed.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs font-mono text-white/60 space-y-1">
                  <span className="uppercase tracking-widest">Registration Fee (GST) ₹</span>
                  <input type="number" min="0" value={form.registration_fee ?? 0} onChange={(e) => setForm({ ...form, registration_fee: e.target.value })}
                    data-testid="batch-registration-fee"
                    className="w-full bg-transparent border border-white/20 py-2.5 px-3 text-sm text-white" />
                </label>
                <label className="text-xs font-mono text-white/60 space-y-1">
                  <span className="uppercase tracking-widest">Admission Fee (GST) ₹</span>
                  <input type="number" min="0" value={form.admission_fee ?? 0} onChange={(e) => setForm({ ...form, admission_fee: e.target.value })}
                    data-testid="batch-admission-fee"
                    className="w-full bg-transparent border border-white/20 py-2.5 px-3 text-sm text-white" />
                </label>
                <label className="text-xs font-mono text-white/60 space-y-1">
                  <span className="uppercase tracking-widest">Kit / Uniform Fee (GST) ₹</span>
                  <input type="number" min="0" value={form.kit_fee ?? 0} onChange={(e) => setForm({ ...form, kit_fee: e.target.value })}
                    data-testid="batch-kit-fee"
                    className="w-full bg-transparent border border-white/20 py-2.5 px-3 text-sm text-white" />
                </label>
                <label className="text-xs font-mono brand-orange space-y-1">
                  <span className="uppercase tracking-widest">Security Deposit · REFUNDABLE · NO GST ₹</span>
                  <input type="number" min="0" value={form.security_deposit ?? 0} onChange={(e) => setForm({ ...form, security_deposit: e.target.value })}
                    data-testid="batch-security-deposit"
                    className="w-full bg-transparent border border-brand-orange/40 py-2.5 px-3 text-sm text-white" />
                </label>
              </div>
            </div>

            {/* Fee Structures editor */}
            <div className="mt-6 border-t border-white/12 pt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-xs uppercase tracking-widest brand-orange flex items-center gap-1"><Layers size={12} /> Fee structures</div>
                <button type="button" onClick={addFee} data-testid="add-fee-structure" className="flex items-center gap-1 text-xs font-mono brand-blue hover:underline"><PlusCircle size={12} /> Add</button>
              </div>
              <p className="text-[11px] text-white/50 mb-3">Add multiple options — e.g. Monthly, Quarterly, Annual, Advanced-batch. Admin picks one when enrolling a ward.</p>
              <div className="space-y-3">
                {(form.fee_structures || []).map((fs, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 border border-white/12 p-3" data-testid={`fee-structure-row-${i}`}>
                    <input value={fs.code} onChange={(e) => updateFee(i, "code", e.target.value)} placeholder="code" data-testid={`fee-code-${i}`} className="col-span-3 bg-transparent border border-white/20 py-2 px-3 text-xs font-mono" />
                    <input value={fs.label} onChange={(e) => updateFee(i, "label", e.target.value)} placeholder="Label" data-testid={`fee-label-${i}`} className="col-span-3 bg-transparent border border-white/20 py-2 px-3 text-xs" />
                    <input type="number" value={fs.amount} onChange={(e) => updateFee(i, "amount", e.target.value)} placeholder="₹ amount" data-testid={`fee-amount-${i}`} className="col-span-3 bg-transparent border border-white/20 py-2 px-3 text-xs" />
                    <input value={fs.period_label || ""} onChange={(e) => updateFee(i, "period_label", e.target.value)} placeholder="per period" className="col-span-2 bg-transparent border border-white/20 py-2 px-3 text-xs" />
                    <button type="button" onClick={() => delFee(i)} className="col-span-1 flex items-center justify-center text-red-400 hover:text-red-300"><MinusCircle size={14} /></button>
                    <input value={fs.notes || ""} onChange={(e) => updateFee(i, "notes", e.target.value)} placeholder="Notes (optional)" className="col-span-12 bg-transparent border border-white/10 py-2 px-3 text-xs text-white/60" />
                  </div>
                ))}
              </div>
            </div>

            <button onClick={save} data-testid="batch-save" className="mt-6 w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest">Save Batch</button>
          </div>
        </div>
      )}
    </div>
  );
}
